import { AnimatePresence, motion } from "framer-motion";
import {
  Brain,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  MessageCircle,
  Palette,
  Quote,
  Rocket,
  Target,
} from "lucide-react";
import { useEffect, useState } from "react";

export const Homepage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const sliderImages = [
    "/images/Code-Right-Website-Slider-1a.png",
    "/images/Code-Right-Website-Slider-1b.png",
    "/images/Code-Right-Website-Slider-1c.png",
  ];

  const testimonials = [
    {
      quote:
        "My child comes home excited to explain the logic behind the games. The screen-free approach keeps her curious and focused.",
      name: "Priya M.",
      role: "Parent of a 6-year-old",
    },
    {
      quote:
        "The teamwork and storytelling challenges have boosted my son's confidence. He talks about solving problems step by step now.",
      name: "Rohan K.",
      role: "Parent of a 7-year-old",
    },
    {
      quote: "We loved the trial class. It was engaging, hands-on, and truly different from typical coding classes.",
      name: "Neha S.",
      role: "Parent of a 5-year-old",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const features = [
    {
      title: "Builds problem-solving and reasoning skills",
      description:
        "Coding for kids is a fundamentally creative process. It allows them to express their imagination with an infinite assemblage of virtual resources. Such an atmosphere is conducive for innovation",
      icon: <Brain className="w-12 h-12 text-secondary" />,
    },
    {
      title: "Encourages logical and structured thinking",
      description:
        "With an introduction to coding, children learn to think laterally when faced with a problem. It helps them resolve intricate exercises by trying out different solutions. This skill helps them to respond and adapt to adversity.",
      icon: <Target className="w-12 h-12 text-secondary" />,
    },
    {
      title: "Boosts creativity through hands-on play",
      icon: <Palette className="w-12 h-12 text-secondary" />,
    },
    {
      title: "Improves communication and teamwork",
      icon: <MessageCircle className="w-12 h-12 text-secondary" />,
    },
    {
      title: "Prepares kids for coding and future STEM learning",
      icon: <Rocket className="w-12 h-12 text-secondary" />,
    },
  ];

  const methodologyImages = [
    { src: "/images/Computer-Science-Code-Right.jpg", label: "Computer Science" },
    { src: "/images/Computer-Programming-Code-Right.jpg", label: "Computer Programming" },
    { src: "/images/Computational-Thinking-Code-Right.jpg", label: "Computational Thinking" },
    { src: "/images/Relationship-Between-These-Code-Right.png", label: "The Relationship" },
  ];

  const faqs = [
    {
      question: "What is coding?",
      answer:
        "Coding is the ability to instruct the computer to work as you please by translating lines of text into actions.",
    },
    {
      question: "Does this program encourage more screen time?",
      answer:
        'CodeRiite is primarily an offline programming course for children. However, we only conduct ten minutes of "Good screen time" each class which stimulates the child\'s brain to think creatively of an idea and execute logically.',
    },
    {
      question: "Why should my child learn Coding?",
      answer:
        "Coding teaches one to think logically and can also help develop creativity. By planning out the actions step-by-step, one needs to think through the process and make it most efficient to achieve the final results. Most importantly, almost all technology that we interact with today are a result of coding.",
    },
    {
      question: "What is an appropriate age for my child to learn coding?",
      answer:
        "Kids begin to understand logic, three to five steps commands and difference between reality and fantasy at the age of four. These are important concepts for learning programming. Our program fosters logical thinking and creativity through computational thinking and concepts such as sequencing and loops.",
    },
    {
      question: "Do you offer trial classes?",
      answer:
        "Yes! We offer trial classes so parents and children can experience our teaching approach before enrolling.",
    },
    {
      question: "How are the classes conducted?",
      answer: (
        <div className="space-y-3">
          <p>Classes are conducted through:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Interactive games and puzzles</li>
            <li>Teamwork</li>
            <li>Hands-on challenges</li>
            <li>Story-based problem solving</li>
            <li>Screenless and word-free electronic coding set</li>
          </ul>
          <p>Sessions are designed to keep children actively involved, not passive.</p>
        </div>
      ),
    },
    {
      question: "Will my child eventually use computers for coding?",
      answer:
        "Yes. Screen-free learning builds a strong conceptual base. Children who understand the logic of coding find it much easier to transition to block-based or text-based coding later.",
    },
    {
      question: "Does my child need any prior coding experience?",
      answer: "No prior experience is needed. The program is beginner-friendly and suitable for first-time learners.",
    },
    {
      question: "Is this program aligned with school learning?",
      answer:
        "Yes. Computational thinking supports school subjects like math, science, and reasoning, helping children perform better academically.",
    },
  ];

  const thinkingConcepts = [
    {
      title: "Decomposition",
      description: "Breaking down a big problem into small, manageable parts.",
      image: "images/ct-1.png",
    },
    {
      title: "Pattern Recognition",
      description:
        "Observing similarities and patterns in these smaller parts, to help us solve complex problems more efficiently.",
      image: "images/ct-2.png",
    },
    {
      title: "Abstraction",
      description: "Identifying and extracting the important parts of the problem.",
      image: "images/ct-3.png",
    },
    {
      title: "Algorithmic Design",
      description: "Creating the step-by-step instructions of solving the problem.",
      image: "images/ct-4.png",
    },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Slider Section */}
      <div className="relative w-full bg-gray-50">
        <div className="grid grid-cols-1 grid-rows-1">
          {sliderImages.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentSlide ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="col-start-1 row-start-1 w-full"
            >
              <img src={img} alt={`Slide ${index + 1}`} className="w-full h-auto object-contain block" />
            </motion.div>
          ))}
        </div>

        <div className="absolute inset-0 flex flex-col justify-end items-center pb-8 z-10 pointer-events-none">
          <div className="flex space-x-3 pointer-events-auto">
            {sliderImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all duration-300 shadow-sm ${
                  index === currentSlide ? "bg-primary w-8" : "bg-white w-3 hover:bg-primary/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Screen-free Coding Section */}
      <section className="py-16 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 text-[1.05rem]">
            <h2 className="text-3xl font-bold text-gray-800">Screen-free Coding and Computational Thinking</h2>
            <p className="text-gray-600 leading-relaxed">
              Screen-free coding introduces children to the logic and creativity behind programming —{" "}
              <span className="font-semibold">without using computers, tablets, or phones.</span>
            </p>
            <p className="text-gray-600 leading-relaxed">
              Through fun, hands-on activities like puzzles, games, and storytelling, kids learn to think like
              programmers:{" "}
              <span className="font-semibold">
                breaking problems into steps, finding patterns, and building solutions.
              </span>
            </p>
            <p className="text-gray-600 leading-relaxed">
              <span className="font-semibold">It’s all about developing computational thinking</span> — a powerful skill
              that helps the kids understand how to solve problems logically and creatively in any area of life.
            </p>
          </div>
          <div className="relative">
            {/* Placeholder for the kids image if not available, or use one of the slider images as fallback */}
            <img
              src="/images/smiling-children-practice-counting.jpg"
              alt="Kids Coding"
              className="rounded-lg shadow-xl w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* What is Computational Thinking Section */}
      <section className="py-20 px-4 bg-linear-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What is Computational Thinking?</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              Computational thinking is a way of solving problems — just like a computer would!
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              It doesn’t mean using a computer; it means thinking logically and step by step to understand problems and
              find smart solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {thinkingConcepts.map((concept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className=" rounded-2xl bg-gray-50 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="h-48 overflow-hidden border-b border-slate-200">
                  <img
                    src={concept.image}
                    alt={concept.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {concept.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{concept.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Computational Thinking Quote */}
          <div className="mt-16 shadow-lg border-slate-100 border rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center max-w-6xl mx-auto">
              <div className="relative">
                <img
                  src="/images/Jeannette-Wing-CompThinking.jpg"
                  alt="Computational Thinking"
                  className="rounded-2xl shadow-lg w-full h-auto object-cover"
                />
              </div>
              <div className="space-y-4 col-span-2">
                <Quote className="w-12 h-12 text-primary/40" />
                <p className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium italic">
                  "Computational Thinking is a fundamental skill for everyone, not just for computer scientists. To
                  reading, writing and arithmetic, we should add Computational Thinking to every child's analytical
                  ability."
                </p>
                <p className="text-lg text-gray-600 mt-4">
                  — Jeannette Wing, Executive Vice President for Research at Columbia University
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="bg-secondary py-16 px-4 text-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="text-6xl text-primary font-serif mb-4">“</div>
          <p className="text-xl md:text-2xl font-medium leading-relaxed">
            We at CodeRiite aspire to instill the 21st century skills of Programming and Computational Thinking within
            the young minds through our fun and intriguing curriculum. Children will be introduced to another
            fascinating dimension of technology empowering them to become the 'architects' of the digital era.
          </p>
        </div>
      </section>

      {/* Benefits for Kids Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mt-2">Benefits for Kids</h2>
        </div>

        <div className="flex flex-col items-center justify-center gap-16">
          <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-8 w-full">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-6 text-start p-4 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 h-full border-t-4 border-transparent hover:border-primary group"
              >
                <div className="p-4 bg-orange-50 rounded-full group-hover:bg-primary/10 transition-colors">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p>{feature?.description || ""}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Flow Section */}
      <section className="py-20 px-4 max-w-screen-2xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">How We Teach</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 px-4">
          {methodologyImages.map((item, index) => (
            <motion.div key={index} whileHover={{ y: -8 }} className="flex flex-col items-center group cursor-pointer">
              <div className="bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] mb-6 w-full transition-all duration-300 group-hover:shadow-[0_20px_40px_rgb(0,0,0,0.16)]">
                <img src={item.src} alt={item.label} className="w-full h-auto rounded-lg object-cover" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 text-center group-hover:text-primary transition-colors duration-300">
                {item.label}
              </h3>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-primary font-bold tracking-wider uppercase text-sm">Testimonials</span>
          <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-12">What Parents Say</h2>

          <div className="relative bg-white rounded-3xl shadow-xl px-8 py-12 md:px-16">
            <Quote className="w-10 h-10 text-primary/20 absolute top-6 left-6" />
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                  "{testimonials[currentTestimonial].quote}"
                </p>
                <div>
                  <p className="text-lg font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-sm text-gray-500">{testimonials[currentTestimonial].role}</p>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="p-2 rounded-full border border-gray-200 text-gray-600 hover:text-primary hover:border-primary transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentTestimonial(index)}
                    className={`h-2.5 rounded-full transition-all ${
                      index === currentTestimonial ? "w-8 bg-primary" : "w-2.5 bg-gray-300 hover:bg-primary/60"
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="p-2 rounded-full border border-gray-200 text-gray-600 hover:text-primary hover:border-primary transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full py-16 px-4 max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12 border-b-4 border-primary inline-block pb-2">FAQS</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className={`w-full flex justify-between items-center p-6 text-left font-semibold text-lg transition-colors ${
                  openFaq === index ? "text-primary" : "text-gray-800 hover:text-primary"
                }`}
              >
                <span className="pr-4">
                  {index + 1}. {faq.question}
                </span>
                {openFaq === index ? (
                  <ChevronUp className="w-5 h-5 shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 shrink-0 text-gray-400" />
                )}
              </button>
              <AnimatePresence>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Homepage;
